import type { StalledCounts } from "@/lib/offline/syncService";

/**
 * What a device reset should do about a sync queue that would not empty.
 *
 * Reset must never silently destroy captured work, but it must also not be
 * blockable forever — a record the server will never accept would otherwise
 * lock a tablet out of re-provisioning permanently. Telling those two cases
 * apart is the whole job of this function.
 *
 * The stalled counts are passed in already split by cause, because the two are
 * not equivalent to the person being asked to accept the loss: a rejected
 * record is genuinely unsendable, while a parked one is merely waiting for the
 * right colleague and would still go through for them.
 */
export type FlushResult =
  | { ok: true }
  /** Something might still land on a better connection: refuse and say so. */
  | { ok: false; reason: "retryable" }
  /** Nothing left can be sent from here: proceed only once a manager accepts it. */
  | { ok: false; reason: "unsendable"; stalled: StalledCounts; count: number };

export function classifyFlush(status: {
  pendingCount: number;
  stalled: StalledCounts;
}): FlushResult {
  if (status.pendingCount === 0) return { ok: true };

  const count = status.stalled.rejected + status.stalled.parked;

  // Both counts come from the same queue table, the stalled one under a
  // narrower predicate, so anything outside it is still worth another attempt.
  if (status.pendingCount > count) {
    return { ok: false, reason: "retryable" };
  }

  return { ok: false, reason: "unsendable", stalled: status.stalled, count };
}
